import { LeaseChargeType } from '@common/entities';
import { CartCharge, CoreChargeDate } from 'state/slices/shoppingCartSlice';

export interface InstallmentChargeGroup {
  id: number;
  number: number;
  charges: CartCharge[];
  startDate: Date;
  endDate: Date;
  total: number;
  currencyCode?: string;
  arTriggerId?: number;
}

interface ParsedCoreChargeDate {
  date: Date;
  timeZone?: string;
}

export function isInstallmentCharge(
  charge: CartCharge,
): charge is CartCharge & { installmentId: number } {
  return typeof charge.installmentId === 'number' && charge.installmentId > 0;
}

export function getInstallmentNumbers(charges: CartCharge[]): Map<number, number> {
  const installments = new Map<number, number>();

  charges.forEach((charge) => {
    if (!isInstallmentCharge(charge)) return;

    const startDate = parseCoreChargeDate(charge.chargeStartDate)?.date.getTime();
    const sortableStartDate = startDate ?? Number.MAX_SAFE_INTEGER;
    const existingStartDate = installments.get(charge.installmentId);
    if (existingStartDate === undefined || sortableStartDate < existingStartDate) {
      installments.set(charge.installmentId, sortableStartDate);
    }
  });

  return new Map(
    Array.from(installments.entries())
      .sort(
        ([firstId, firstDate], [secondId, secondDate]) =>
          firstDate - secondDate || firstId - secondId,
      )
      .map(([installmentId], index) => [installmentId, index + 1]),
  );
}

export function addInstallmentNumbers(charges: CartCharge[]): CartCharge[] {
  const installmentNumbers = getInstallmentNumbers(charges);

  return charges.map((charge) => {
    if (!isInstallmentCharge(charge)) return charge;

    const installmentNumber = installmentNumbers.get(charge.installmentId);
    if (!installmentNumber) return charge;

    return {
      ...charge,
      installmentNumber,
    };
  });
}

function parseCoreChargeDate(
  value?: string | CoreChargeDate | null,
): ParsedCoreChargeDate | undefined {
  if (!value) return undefined;

  let parsed: string | CoreChargeDate = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value) as string | CoreChargeDate;
    } catch {
      // Older responses may already contain the raw date value.
    }
  }

  if (typeof parsed !== 'string' && (typeof parsed !== 'object' || parsed === null)) {
    return undefined;
  }

  const dateValue = typeof parsed === 'string' ? parsed : parsed.date;
  let timeZone: string | undefined;
  if (typeof parsed !== 'string') {
    timeZone = parsed.timezone_name;
  }

  if (!dateValue) return undefined;

  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(dateValue);
  if (!match) return undefined;

  const [, month, day, year] = match;
  return {
    date: new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))),
    timeZone,
  };
}

function getTodayInTimeZone(now: Date, timeZone?: string): Date {
  if (!timeZone) {
    return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(now);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);

  return new Date(Date.UTC(value('year'), value('month') - 1, value('day')));
}

function getChargeOriginId(charge: CartCharge): number {
  return charge.allChargesDetails?.[0]?.arOriginId ?? Number.MAX_SAFE_INTEGER;
}

function addUtcMonths(date: Date, months: number): Date {
  const targetYear = date.getUTCFullYear();
  const targetMonth = date.getUTCMonth() + months;
  const lastDayOfTargetMonth = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();

  return new Date(
    Date.UTC(targetYear, targetMonth, Math.min(date.getUTCDate(), lastDayOfTargetMonth)),
  );
}

export function getInstallmentChargeGroups(
  charges: CartCharge[],
  now = new Date(),
): InstallmentChargeGroup[] {
  const groups = new Map<number, CartCharge[]>();

  charges.forEach((charge) => {
    if (!isInstallmentCharge(charge)) return;
    const group = groups.get(charge.installmentId) ?? [];
    group.push(charge);
    groups.set(charge.installmentId, group);
  });
  const installmentNumbers = getInstallmentNumbers(charges);

  return Array.from(groups.entries())
    .flatMap(([id, groupCharges]) => {
      const start = parseCoreChargeDate(groupCharges[0]?.chargeStartDate);
      const end = parseCoreChargeDate(groupCharges[0]?.chargeEndDate);
      if (!start || !end) return [];

      const today = getTodayInTimeZone(now, start.timeZone);
      const hasOutstandingBalance = groupCharges.some(
        (charge) => charge.dueNowAmount !== undefined && charge.dueNowAmount !== 0,
      );
      if (end.date < today && !hasOutstandingBalance) return [];

      return [
        {
          id,
          number: installmentNumbers.get(id) ?? 1,
          charges: [...groupCharges].sort(
            (first, second) => getChargeOriginId(first) - getChargeOriginId(second),
          ),
          startDate: start.date,
          endDate: end.date,
          total: groupCharges.reduce(
            (sum, charge) => sum + Number(charge.totalValueWithTax ?? 0),
            0,
          ),
          currencyCode: groupCharges[0]?.currencyCode,
          arTriggerId: groupCharges[0]?.arTriggerId,
        },
      ];
    })
    .sort((first, second) => first.startDate.getTime() - second.startDate.getTime());
}

export type InstallmentDurationUnit = 'week' | 'month';

export interface InstallmentDuration {
  unit: InstallmentDurationUnit;
  count: number;
  remainingDays: number;
}

export function getInstallmentDuration(
  startDate: Date,
  endDate: Date,
  arTriggerId?: number,
): InstallmentDuration {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const inclusiveDays =
    Math.round((endDate.getTime() - startDate.getTime()) / millisecondsPerDay) + 1;

  if (arTriggerId === LeaseChargeType.Monthly) {
    const exclusiveEndDate = new Date(endDate.getTime() + millisecondsPerDay);
    let months = 0;
    while (addUtcMonths(startDate, months + 1) <= exclusiveEndDate) {
      months += 1;
    }

    const monthEndDate = addUtcMonths(startDate, months);
    if (monthEndDate.getTime() === endDate.getTime()) {
      return { unit: 'month', count: months, remainingDays: 0 };
    }

    return {
      unit: 'month',
      count: months,
      remainingDays: Math.round(
        (exclusiveEndDate.getTime() - monthEndDate.getTime()) / millisecondsPerDay,
      ),
    };
  }

  return {
    unit: 'week',
    count: Math.floor(inclusiveDays / 7),
    remainingDays: inclusiveDays % 7,
  };
}
